/**
 * Cleanup-strategy docs — moved off the Command Center dashboard
 * (was an inline paragraph at the bottom; documentation, not
 * dashboard content). The Maintenance card on
 * /admin/operations/profiles links here.
 */
import React from 'react';
import {Box, Stack, Typography, Divider, Paper} from '@mui/material';
import {Link as RouterLink} from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {usePageTitle} from '../../hooks/usePageTitle';
import {pageSx, pageTitleSx} from '../../theme/planapusTheme';

export default function CleanupStrategyDocs(): React.JSX.Element {
  usePageTitle('Admin — Cleanup strategy');
  return (
    <Box sx={pageSx}>
      <Box
        component={RouterLink}
        to="/admin/operations/profiles"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: 12,
          color: '#4f46e5',
          textDecoration: 'none',
          mb: 2,
          '&:hover': {color: '#3730a3'},
        }}
      >
        <ArrowBackIcon sx={{fontSize: 14}} />
        Back to Command center
      </Box>
      <Typography sx={pageTitleSx}>Cleanup strategy</Typography>
      <Typography sx={{fontSize: 13, color: '#64748b', mt: 0.5, mb: 3}}>
        How sessions accumulate, and the three ways to keep the table tidy.
      </Typography>

      <Paper variant="outlined" sx={{p: 3, borderRadius: 2}}>
        <Typography sx={{fontSize: 14, color: '#334155', lineHeight: 1.7}}>
          Once <code>expiresAt</code> is in the past, the session's cookies are dead — the
          desktop can't use them for any task. Old rows accumulate naturally as Google rotates
          the SID cookie, the user logs in from new devices, or the user logs out and back in.
          Three ways to keep the <code>sessions</code> collection tidy:
        </Typography>

        <Divider sx={{my: 2.5}} />

        <Stack spacing={2.5}>
          <Option
            n={1}
            title="Hard-delete sweep"
            body={
              <>
                A cleanup script (like the edge-task one we ship) drops every row with{' '}
                <code>expiresAt &lt; now</code>.{' '}
                <strong>Pros:</strong> simplest. <strong>Cons:</strong> loses audit history — you
                can't answer "when did this user last log in?" once the row is gone.
              </>
            }
          />
          <Option
            n={2}
            title="Keep-most-recent-per-profile"
            recommended
            body={
              <>
                Same sweep, but keep the one row per profile with the latest{' '}
                <code>lastSeenAt</code>, even if expired. Preserves "last successful login"
                forensics with bounded storage growth. <strong>Recommended for GoodShowHQ today</strong>{' '}
                — wrap it in a one-shot script you can run from your laptop, mirroring{' '}
                <code>scripts/cleanup-edge-tasks.ts</code>.
              </>
            }
          />
          <Option
            n={3}
            title="Firestore TTL policy"
            body={
              <>
                Native Firestore feature — set a TTL on the <code>sessions</code> collection keyed
                on <code>expiresAt</code>. Firestore auto-drops rows after the timestamp.{' '}
                <strong>Pros:</strong> free, no code. <strong>Cons:</strong> daily-ish granularity
                and you can't add a grace period.
              </>
            }
          />
        </Stack>

        <Divider sx={{my: 2.5}} />

        <Typography sx={{fontSize: 13, color: '#64748b', lineHeight: 1.7}}>
          <strong>Orphan sessions</strong> (rows whose <code>profileId</code> doesn't resolve to
          any document in the <code>profile</code> collection) are always safe to delete since no
          code path uses them. The Command Center's "Clean up" action on the orphan-sessions row
          does exactly this.
        </Typography>
      </Paper>
    </Box>
  );
}

function Option({
  n,
  title,
  body,
  recommended,
}: {
  n: number;
  title: string;
  body: React.ReactNode;
  recommended?: boolean;
}): React.JSX.Element {
  return (
    <Box sx={{display: 'flex', gap: 2}}>
      <Box
        sx={{
          flexShrink: 0,
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: recommended ? '#d1fae5' : '#f1f5f9',
          color: recommended ? '#047857' : '#475569',
          fontSize: 13,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {n}
      </Box>
      <Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Typography sx={{fontSize: 14, fontWeight: 700, color: '#0f172a'}}>{title}</Typography>
          {recommended && (
            <Box
              sx={{
                fontSize: 9,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                color: '#047857',
                bgcolor: '#d1fae5',
                px: 1,
                py: 0.25,
                borderRadius: 999,
              }}
            >
              Recommended
            </Box>
          )}
        </Box>
        <Typography sx={{fontSize: 13, color: '#475569', mt: 0.5, lineHeight: 1.6}}>{body}</Typography>
      </Box>
    </Box>
  );
}
